'use client'
import { FC } from 'react'
import cn from 'classnames'
import { getAvailabilityLevel } from '@/root/modules/shared/SelectDateTimePage'
import { SoldOutIcon } from '@/root/ui/tuuleliinid/icons/SoldOutIcon'

interface AvailabilityCircleProps {
  amount: number
  total: number
  isSelected: boolean
  insufficientCapacity?: boolean
  testId?: string
}

// SVG circle constants
const SIZE = 40
const STROKE_WIDTH = 4
const RADIUS = (SIZE - STROKE_WIDTH) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

// Colors matching
const COLORS = {
  progress: {
    green: '#22c55e', // success-medium-light
    yellow: '#eab308', // warning
    red: '#ef4444', // error-medium-light
  },
  background: {
    default: '#e6e6e6', // neutral-very-light
    selected: '#0464bc', // tertiary-dark
  },
}

export const AvailabilityCircle: FC<AvailabilityCircleProps> = ({ amount, total, isSelected, insufficientCapacity = false, testId }) => {
  const { level, displayPercent } = getAvailabilityLevel(amount, total)
  const strokeDashoffset = CIRCUMFERENCE - (displayPercent / 100) * CIRCUMFERENCE

  const progressColorByLevel = {
    green: COLORS.progress.green,
    yellow: COLORS.progress.yellow,
    red: COLORS.progress.red,
  }

  const getProgressColor = () => {
    if (insufficientCapacity) {
      return level === 'red' ? COLORS.progress.red : '#9e9e9e'
    }
    return progressColorByLevel[level]
  }

  const bgColor = insufficientCapacity ? COLORS.background.default : isSelected ? COLORS.background.selected : COLORS.background.default

  return (
    <div className="relative flex h-10 w-11 items-center justify-center">
      <svg width={SIZE} height={SIZE} className="rotate-90">
        {/* Background circle */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={bgColor}
          strokeWidth={STROKE_WIDTH}
          className="transition-all duration-300"
        />
        {/* Progress circle */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={getProgressColor()}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      {/* Center content */}
      <div
        data-testid={testId}
        className={cn('absolute text-center text-lg leading-none transition-colors duration-300', {
          'text-white': isSelected,
          'text-neutral-medium-light': insufficientCapacity,
        })}
      >
        {amount === 0 ? <SoldOutIcon width={24} height={24} /> : amount}
      </div>
    </div>
  )
}
